import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import { getToken } from '../utils/getToken';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://devapi.quickcare.ng/admin';

ChartJS.register(
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Filler,
    Tooltip,
    Legend
);

const PatientCharts = () => {
    const [monthlySignups, setMonthlySignups] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMonthlySignups = async () => {
            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");

            const token = getToken();
            if (token) {
                myHeaders.append("Authorization", `Bearer ${token}`);
            } else {
                console.error('No token found');
                setError('Authentication failed');
                setLoading(false);
                return;
            }

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };

            try {
                const response = await fetch(`${baseURL}/patients/monthly-signups`, requestOptions);
                const result = await response.json();
                console.log('Server response:', result);

                if (result.data) {
                    const signups = [
                        result.data.January || 0,
                        result.data.February || 0,
                        result.data.March || 0,
                        result.data.April || 0,
                        result.data.May || 0,
                        result.data.June || 0,
                        result.data.July || 0,
                        result.data.August || 0,
                        result.data.September || 0,
                        result.data.October || 0,
                        result.data.November || 0,
                        result.data.December || 0
                    ];
                    setMonthlySignups(signups);
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching monthly signups:', error);
                setError('Failed to load chart data');
                setLoading(false);
            }
        };

        fetchMonthlySignups();
    }, []);

    // Find max value for dynamic y-axis
    const maxValue = Math.max(...monthlySignups, 10);
    const suggestedMax = Math.ceil(maxValue / 10) * 10 + 10;

    const data = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        datasets: [
            {
                label: 'Monthly Signups',
                data: monthlySignups,
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
                    gradient.addColorStop(0, 'rgba(6, 145, 37, 0.3)');
                    gradient.addColorStop(1, 'rgba(6, 145, 37, 0.01)');
                    return gradient;
                },
                borderColor: '#069125',
                borderWidth: 3,
                pointBackgroundColor: '#069125',
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 7,
                pointHoverBackgroundColor: '#057a1f',
                tension: 0.4,
                fill: true,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 20,
                    font: {
                        size: 12,
                        weight: '600',
                        family: 'Inter, system-ui, sans-serif'
                    },
                    color: '#374151'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#1a1a1a',
                bodyColor: '#374151',
                bodyFont: {
                    size: 13,
                    weight: '500'
                },
                titleFont: {
                    size: 14,
                    weight: '700'
                },
                padding: 12,
                borderColor: '#069125',
                borderWidth: 2,
                cornerRadius: 12,
                displayColors: false,
                callbacks: {
                    label: function(context) {
                        return `👤 ${context.parsed.y} patients`;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.06)',
                    drawBorder: false,
                },
                ticks: {
                    font: {
                        size: 12,
                        weight: '500',
                        family: 'Inter, system-ui, sans-serif'
                    },
                    color: '#6B7280',
                }
            },
            y: {
                min: 0,
                max: suggestedMax,
                ticks: {
                    stepSize: Math.ceil(suggestedMax / 5),
                    callback: (value) => value + '',
                    font: {
                        size: 11,
                        weight: '500',
                        family: 'Inter, system-ui, sans-serif'
                    },
                    color: '#6B7280',
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.06)',
                    drawBorder: false,
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
        elements: {
            line: {
                tension: 0.4,
            },
            point: {
                hoverRadius: 7,
            }
        },
        animation: {
            duration: 800,
            easing: 'easeInOutQuart'
        }
    };

    if (loading) {
        return (
            <div className="w-full h-64 lg:h-80 flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#069125]"></div>
                <p className="text-sm text-gray-500 mt-4">Loading chart data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full h-64 lg:h-80 flex flex-col items-center justify-center text-center">
                <span className="text-4xl mb-3">⚠️</span>
                <h3 className="text-lg font-semibold text-gray-700">Error Loading Chart</h3>
                <p className="text-sm text-gray-500 mt-1">{error}</p>
                <button 
                    onClick={() => window.location.reload()}
                    className="mt-4 px-6 py-2 bg-[#069125] text-white text-sm font-medium rounded-xl hover:bg-[#057a1f] transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    // Check if there's any data
    const hasData = monthlySignups.some(value => value > 0);

    if (!hasData) {
        return (
            <div className="w-full h-64 lg:h-80 flex flex-col items-center justify-center text-center">
                <span className="text-4xl mb-3">📊</span>
                <h3 className="text-lg font-semibold text-gray-700">No Data Available</h3>
                <p className="text-sm text-gray-500 mt-1">No patient signups recorded yet</p>
            </div>
        );
    }

    // Calculate total for summary
    const totalPatients = monthlySignups.reduce((sum, val) => sum + val, 0);
    const averagePatients = Math.round(totalPatients / monthlySignups.filter(v => v > 0).length || 0);
    const peakMonth = Math.max(...monthlySignups);
    const peakMonthIndex = monthlySignups.indexOf(peakMonth);
    const peakMonthName = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][peakMonthIndex];

    return (
        <div className="w-full">
            {/* Chart Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-blue-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Total Patients</p>
                    <p className="text-lg font-bold text-blue-600">{totalPatients}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Monthly Avg</p>
                    <p className="text-lg font-bold text-green-600">{averagePatients}</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Peak Month</p>
                    <p className="text-lg font-bold text-purple-600">{peakMonthName}</p>
                </div>
                <div className="bg-orange-50 rounded-xl p-3">
                    <p className="text-xs text-gray-500">Peak Value</p>
                    <p className="text-lg font-bold text-orange-600">{peakMonth}</p>
                </div>
            </div>

            {/* Chart */}
            <div className="w-full h-64 lg:h-80">
                <Line 
                    data={data} 
                    options={options}
                    height={300}
                />
            </div>

            {/* Chart Footer */}
            <div className="mt-4 pt-3 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span>📈</span>
                    <span>Monthly patient signup trends</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span>Last 12 months</span>
                    <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                    <span>Live data</span>
                </div>
            </div>
        </div>
    );
};

export default PatientCharts;